/**
 * The animated half of the tower: 4096 parcels, one draw call, drawn from the data textures.
 *
 * `tools/atlas.ts` bakes sixteen 2048px pages of *stills* and `src/main.ts` draws them as sixteen
 * textured meshes. This draws the same collection *moving*: the five textures `tools/towerdata.ts`
 * bakes are the art itself — cells, palettes, schedules, block means and the glyph atlas — and the
 * shader in `@terra-robin/core`'s `emit/glsl.ts` composites one frame of one token per fragment.
 * Nothing about the picture is decided here. This module is the plumbing: fetch the bytes, hand
 * them to the GPU in the exact formats the layout assumes, and give the app a handle on the two
 * things that change per draw — which frame the clock is on, and how much detail to ask for.
 *
 * **One `InstancedMesh` for the whole collection.** A parcel is four vertices, so the cost is all
 * in the fragment stage and in the driver's per-draw overhead; 4096 draw calls would spend more
 * time in JavaScript than in the shader. Everything that varies per parcel is an instanced
 * attribute (`TOWER_INSTANCE_ATTRIBUTES`), and the two things that vary per *frame* are uniforms
 * (`TOWER_FRAME_UNIFORMS`), which is why sixteen floors of animation cost one `drawElements`.
 *
 * **The formats are load-bearing, not defaults.** `cells` and `schedules` are integer textures:
 * `R8UI` and `RGBA32UI`, `NEAREST` and `CLAMP_TO_EDGE`, no mip chain — an integer texture with a
 * filtered minification is incomplete, and an incomplete texture does not error, it fetches zero,
 * which would draw a plausible-looking wrong picture. `palettes` is raw `RGBA8` with no colour
 * space tag, because the whole collection composites in sRGB *byte* space to match the payload's
 * canvas (see `emit/glsl.ts`). `blocks` and `glyphs` are the two that want mips, for opposite
 * reasons: the block texture's levels 1..3 are a token's 8-, 16- and 32-cell means, and the glyph
 * atlas's levels are a mark's prefiltered coverage, which is what keeps a distant parcel from
 * aliasing into noise.
 *
 * **The clock reads boundaries, not durations.** `frameStartMs` floors, so frame 1 starts at 83ms
 * and not at 83.33; asking `floor(t * 12 / 1000)` would say frame 0 at t=83 while the payload's
 * player is already on frame 1. That is twelve milliseconds of disagreement per second between two
 * renderers of the same token, so `frameOf` walks the same twelve boundaries the player walks.
 */
import {
  GLITCH_FRAMES,
  GRID_COLS,
  GRID_ROWS,
  LOOP_MS,
  MAX_SUPPLY,
  STILL_FRAME,
  SLOT_COUNT,
  BLOCK_BYTES,
  BLOCK_TEX_PX,
  BLOCK_TOP_LEVEL,
  CELLS_BYTES,
  CELLS_TEX_PX,
  GLYPH_BYTES,
  GLYPH_LAYERS,
  GLYPH_TILE_PX,
  PAL_BYTES,
  PAL_TEX_H,
  PAL_TEX_W,
  SCHED_BYTES,
  SCHED_TEX_H,
  SCHED_TEX_W,
  TOWER_FRAME_UNIFORMS,
  TOWER_INSTANCE_ATTRIBUTES,
  TOWER_TEXTURE_UNIFORMS,
  frameStartMs,
  parcelFragmentGlsl,
  parcelVertexGlsl,
} from "@terra-robin/core";
import {
  ClampToEdgeWrapping,
  DataArrayTexture,
  DataTexture,
  DoubleSide,
  InstancedBufferAttribute,
  InstancedMesh,
  LinearFilter,
  LinearMipmapLinearFilter,
  Matrix4,
  NearestFilter,
  PlaneGeometry,
  RedFormat,
  RedIntegerFormat,
  RGBAFormat,
  RGBAIntegerFormat,
  ShaderMaterial,
  UnsignedByteType,
  UnsignedIntType,
  type PixelFormatGPU,
  type Texture,
  type WebGLRenderer,
} from "three";

/**
 * The internal format each file is uploaded as, spelled once.
 *
 * These strings do double duty: they are compared against `data.json`'s own `format` fields, and
 * three hands them straight to `gl[name]` when a texture sets `internalFormat`. Writing them as a
 * `satisfies PixelFormatGPU` literal means a typo is a compile error rather than a
 * `gl[undefined]` at upload time, and it is the same literal on both sides of the comparison — so
 * the check cannot pass while the upload uses something else.
 */
const GPU_FORMAT = {
  cells: "R8UI",
  palettes: "RGBA8",
  schedules: "RGBA32UI",
  blocks: "RGBA8",
  glyphs: "R8",
} as const satisfies Record<string, PixelFormatGPU>;

/** One texture's descriptor in `data.json`, as far as this module reads it. */
export interface TowerTextureDesc {
  readonly file: string;
  readonly format: string;
  readonly width: number;
  readonly height: number;
  readonly bytes: number;
}

/** `out/tower/data.json` — what the baker published about the bake it just wrote. */
export interface TowerData {
  readonly salt: string;
  readonly supply: number;
  readonly frames: number;
  readonly loopMs: number;
  readonly cols: number;
  readonly rows: number;
  readonly endian: string;
  readonly textures: {
    readonly cells: TowerTextureDesc;
    readonly palettes: TowerTextureDesc;
    readonly schedules: TowerTextureDesc;
    readonly blocks: TowerTextureDesc;
    readonly glyphs: TowerTextureDesc & { readonly layers: number };
  };
  readonly columns: {
    readonly glyphSet: readonly number[];
    readonly corrupt: readonly number[];
    readonly hot: readonly number[];
  };
  readonly coverage: readonly (readonly number[])[];
  readonly outsideBox: readonly (readonly number[])[];
}

/** The five textures, uploaded and ready to bind. */
export interface TowerTextures {
  readonly cells: DataTexture;
  readonly palette: DataTexture;
  readonly sched: DataTexture;
  readonly blocks: DataTexture;
  readonly glyphs: DataArrayTexture;
  /** What the browser actually downloaded, for the status line. */
  readonly bytes: number;
}

function fail(what: string): never {
  throw new Error(`tower data: ${what}`);
}

/** One descriptor against the layout the shader was compiled from. */
function checkDesc(
  name: string,
  got: TowerTextureDesc,
  format: string,
  width: number,
  height: number,
  bytes: number,
): void {
  if (got.format !== format) fail(`${name} is ${got.format}, the shader reads ${format}`);
  if (got.width !== width) fail(`${name} is ${got.width} wide, the layout says ${width}`);
  if (got.height !== height) fail(`${name} is ${got.height} tall, the layout says ${height}`);
  if (got.bytes !== bytes) fail(`${name} claims ${got.bytes} bytes, the layout says ${bytes}`);
}

/**
 * `data.json`, checked against the constants this bundle was compiled with.
 *
 * The app is about to fetch 8.6 MB of raw bytes and use them as addresses. A bake from a different
 * layout — a wider cell texture, a different frame count, a schedule with another header size —
 * would not fail to load, it would draw someone else's data as art. Every check here is one line
 * and every message names both numbers, because "the tower renders noise" is a bad error message.
 */
export async function fetchTowerData(base: string): Promise<TowerData> {
  const res = await fetch(`${base}/data.json`);
  if (!res.ok) fail(`${base}/data.json responded ${res.status}`);
  const data = (await res.json()) as TowerData;

  if (!Number.isInteger(data.supply) || data.supply < 1 || data.supply > MAX_SUPPLY) {
    fail(`supply is ${data.supply}, which is not 1..${MAX_SUPPLY}`);
  }
  if (data.frames !== GLITCH_FRAMES) fail(`the bake has ${data.frames} frames, the shader has ${GLITCH_FRAMES}`);
  if (data.loopMs !== LOOP_MS) fail(`the bake loops in ${data.loopMs}ms, the clock in ${LOOP_MS}ms`);
  if (data.cols !== GRID_COLS || data.rows !== GRID_ROWS) {
    fail(`the bake is ${data.cols}x${data.rows} cells, the shader reads ${GRID_COLS}x${GRID_ROWS}`);
  }
  // `schedules.bin` is 32-bit words in host order and `texImage2D` hands them to the driver as
  // they lie. The baker refuses to write on a big-endian host; this refuses to read one.
  if (data.endian !== "little") fail(`the bake is ${data.endian}-endian`);
  if (new Uint8Array(new Uint32Array([1]).buffer)[0] !== 1) fail("this browser is big-endian");

  for (const [name, column] of Object.entries(data.columns)) {
    if (column.length !== data.supply) {
      fail(`column ${name} has ${column.length} entries for ${data.supply} tokens`);
    }
  }
  const t = data.textures;
  checkDesc("cells.bin", t.cells, GPU_FORMAT.cells, CELLS_TEX_PX, CELLS_TEX_PX, CELLS_BYTES);
  checkDesc("palettes.bin", t.palettes, GPU_FORMAT.palettes, PAL_TEX_W, PAL_TEX_H, PAL_BYTES);
  checkDesc("schedules.bin", t.schedules, GPU_FORMAT.schedules, SCHED_TEX_W, SCHED_TEX_H, SCHED_BYTES);
  checkDesc("blocks.bin", t.blocks, GPU_FORMAT.blocks, BLOCK_TEX_PX, BLOCK_TEX_PX, BLOCK_BYTES);
  checkDesc("glyphs.bin", t.glyphs, GPU_FORMAT.glyphs, GLYPH_TILE_PX, GLYPH_TILE_PX, GLYPH_BYTES);
  if (t.glyphs.layers !== GLYPH_LAYERS) {
    fail(`the atlas has ${t.glyphs.layers} layers, the shader indexes ${GLYPH_LAYERS}`);
  }
  if (t.palettes.width !== SLOT_COUNT) fail(`the palette is ${t.palettes.width} slots, not ${SLOT_COUNT}`);
  return data;
}

/** One texture file, with its length checked against what the descriptor promised. */
async function fetchBytes(base: string, desc: TowerTextureDesc): Promise<Uint8Array> {
  const res = await fetch(`${base}/${desc.file}`);
  if (!res.ok) fail(`${base}/${desc.file} responded ${res.status}`);
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length !== desc.bytes) {
    fail(`${desc.file} is ${bytes.length} bytes, the descriptor says ${desc.bytes}`);
  }
  return bytes;
}

/** Point-sampled, edge-clamped, unmipped — the settings an addressed texture needs. */
function exact<T extends Texture>(tex: T): T {
  tex.magFilter = NearestFilter;
  tex.minFilter = NearestFilter;
  tex.wrapS = ClampToEdgeWrapping;
  tex.wrapT = ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Fetch and upload the five textures.
 *
 * The two integer textures get their internal format spelled out. three would infer `R8UI` from
 * `RED_INTEGER` + `UNSIGNED_BYTE` and `RGBA32UI` from `RGBA_INTEGER` + `UNSIGNED_INT` anyway, but
 * `data.json` names those formats and this is where the name is honoured, so a bake that ever says
 * something else stops here instead of at a wrong pixel.
 */
export async function loadTowerTextures(base: string, data: TowerData): Promise<TowerTextures> {
  const t = data.textures;
  const [cellBytes, palBytes, schedBytes, blockBytes, glyphBytes] = await Promise.all([
    fetchBytes(base, t.cells),
    fetchBytes(base, t.palettes),
    fetchBytes(base, t.schedules),
    fetchBytes(base, t.blocks),
    fetchBytes(base, t.glyphs),
  ]);

  const cells = exact(new DataTexture(cellBytes, t.cells.width, t.cells.height, RedIntegerFormat, UnsignedByteType));
  cells.internalFormat = GPU_FORMAT.cells;

  // No `colorSpace`: these bytes are the hex strings the payload's player assigns to `fillStyle`,
  // and every blend in the shader happens in that space. Tagging them sRGB would have the driver
  // decode to linear and the picture would stop matching the SVG.
  const palette = exact(
    new DataTexture(palBytes, t.palettes.width, t.palettes.height, RGBAFormat, UnsignedByteType),
  );

  // A `Uint32Array` view, because the texture is `RGBA32UI` and the driver reads words.
  const schedWords = new Uint32Array(schedBytes.buffer, schedBytes.byteOffset, schedBytes.byteLength / 4);
  const sched = exact(
    new DataTexture(schedWords, t.schedules.width, t.schedules.height, RGBAIntegerFormat, UnsignedIntType),
  );
  sched.internalFormat = GPU_FORMAT.schedules;

  // The one texture that is fetched by level rather than filtered. `trBlock` asks for levels 0..3
  // explicitly with `texelFetch`, so the filter mode is moot and the *chain* is not: without it
  // levels 1..3 do not exist and every distant parcel reads level 0's four-cell mean instead of
  // its own 8-, 16- and 32-cell ones.
  const blocks = exact(new DataTexture(blockBytes, t.blocks.width, t.blocks.height, RGBAFormat, UnsignedByteType));
  blocks.generateMipmaps = true;
  blocks.needsUpdate = true;

  // The atlas is the opposite: filtered and mipped all the way down, because level 6 of a 64px
  // tile is one texel holding the mark's mean coverage — which is the number the far LOD's block
  // means were baked from, so the two ends of the ladder meet.
  const glyphs = new DataArrayTexture(glyphBytes, t.glyphs.width, t.glyphs.height, t.glyphs.layers);
  glyphs.format = RedFormat;
  glyphs.type = UnsignedByteType;
  glyphs.internalFormat = GPU_FORMAT.glyphs;
  glyphs.magFilter = LinearFilter;
  glyphs.minFilter = LinearMipmapLinearFilter;
  glyphs.wrapS = ClampToEdgeWrapping;
  glyphs.wrapT = ClampToEdgeWrapping;
  glyphs.generateMipmaps = true;
  glyphs.needsUpdate = true;

  const bytes = cellBytes.length + palBytes.length + schedBytes.length + blockBytes.length + glyphBytes.length;
  return { cells, palette, sched, blocks, glyphs, bytes };
}

/** Release the five textures' GPU memory. Separate from `Parcels`, which only borrows them. */
export function disposeTowerTextures(tex: TowerTextures): void {
  tex.cells.dispose();
  tex.palette.dispose();
  tex.sched.dispose();
  tex.blocks.dispose();
  tex.glyphs.dispose();
}

/**
 * Does this GPU have room for the collection?
 *
 * WebGL2 guarantees only 2048 for `MAX_TEXTURE_SIZE`, and the palette texture is one row per token
 * — 4096 tall. So a conforming-but-small device cannot hold this bake at all, and the honest thing
 * is to say which limit it missed by how much rather than to draw a black tower. The array atlas
 * needs 108 layers against a guaranteed 256, and the shader binds five samplers against a
 * guaranteed 16, so those two are checked for completeness rather than suspicion.
 */
export function assertTextureLimits(renderer: WebGLRenderer, data: TowerData): void {
  const gl = renderer.getContext() as WebGL2RenderingContext;
  const t = data.textures;
  const needSize = Math.max(
    t.cells.width, t.cells.height,
    t.palettes.width, t.palettes.height,
    t.schedules.width, t.schedules.height,
    t.blocks.width, t.blocks.height,
    t.glyphs.width, t.glyphs.height,
  );
  const maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
  if (maxSize < needSize) {
    fail(`this GPU's MAX_TEXTURE_SIZE is ${maxSize} and the bake needs ${needSize}`);
  }
  const maxLayers = gl.getParameter(gl.MAX_ARRAY_TEXTURE_LAYERS) as number;
  if (maxLayers < t.glyphs.layers) {
    fail(`this GPU allows ${maxLayers} array layers and the atlas has ${t.glyphs.layers}`);
  }
  const units = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number;
  if (units < TOWER_TEXTURE_UNIFORMS.length) {
    fail(`this GPU offers ${units} fragment texture units and the shader binds ${TOWER_TEXTURE_UNIFORMS.length}`);
  }
}

/** How a parcel presents itself this instant: drawn at all, and how bright. */
export interface Presentation {
  /** False collapses the instance before rasterisation — see `aShow` in `emit/glsl.ts`. */
  readonly show: boolean;
  /** Multiplied into the composite. The tower's dimmed state is 0.07, never 0. */
  readonly dim: number;
}

export interface ParcelsOptions {
  readonly data: TowerData;
  readonly textures: TowerTextures;
  /** Instance to token id. The tower's census decides the order; this only draws it. */
  readonly ids: ArrayLike<number>;
}

/**
 * The tower's animated parcels: one mesh, one material, five textures and a clock.
 *
 * The material is a plain `ShaderMaterial` and `glslVersion` is deliberately left unset. The
 * emitted text is ES 3.00 either way — three gives a `ShaderMaterial` `#version 300 es` — but the
 * `out` declaration behind `gl_FragColor` only appears in three's prefix on the branch where
 * `glslVersion` is *not* `GLSL3`. Setting it to the version the shader is actually written in
 * would fail to compile, which is the sort of thing worth writing down once.
 */
export class Parcels {
  readonly mesh: InstancedMesh;
  readonly material: ShaderMaterial;
  /** Instance to token id, for turning a raycast hit back into a token. */
  readonly ids: Int32Array;

  private readonly dimAttr: InstancedBufferAttribute;
  private readonly showAttr: InstancedBufferAttribute;
  private readonly bounds: number[] = [];
  private readonly matrix = new Matrix4();

  constructor(opts: ParcelsOptions) {
    const { data, textures, ids } = opts;
    const count = ids.length;
    if (count < 1) fail("a tower with no parcels");
    this.ids = new Int32Array(count);

    const aId = new Float32Array(count);
    const aGlyphSet = new Float32Array(count);
    const aCorrupt = new Float32Array(count);
    const aDim = new Float32Array(count).fill(1);
    const aShow = new Float32Array(count).fill(1);
    for (let i = 0; i < count; i++) {
      const id = ids[i] as number;
      if (!Number.isInteger(id) || id < 0 || id >= data.supply) {
        fail(`instance ${i} is token ${id}, and the bake holds 0..${data.supply - 1}`);
      }
      this.ids[i] = id;
      aId[i] = id;
      // `data.json`'s canonical index into `GLYPH_SETS`, not `manifest.json`'s dictionary code:
      // that one is a first-appearance ordering for the filter menu and would pick wrong marks.
      aGlyphSet[i] = data.columns.glyphSet[id] as number;
      aCorrupt[i] = data.columns.corrupt[id] as number;
    }

    // A unit quad lying flat in XZ, facing up — the same orientation `src/main.ts` gives the
    // stills, so a parcel's top edge points at world -Z and reads upright from +Z. Not shared
    // with anything: instanced attributes live on the geometry, and a shared one would hand
    // `aId` to every other mesh built from it.
    const geometry = new PlaneGeometry(1, 1);
    geometry.rotateX(-Math.PI / 2);
    this.dimAttr = new InstancedBufferAttribute(aDim, 1);
    this.showAttr = new InstancedBufferAttribute(aShow, 1);
    geometry.setAttribute("aId", new InstancedBufferAttribute(aId, 1));
    geometry.setAttribute("aGlyphSet", new InstancedBufferAttribute(aGlyphSet, 1));
    geometry.setAttribute("aCorrupt", new InstancedBufferAttribute(aCorrupt, 1));
    geometry.setAttribute("aDim", this.dimAttr);
    geometry.setAttribute("aShow", this.showAttr);
    // The shader's own list is the checklist: a rename in `emit/glsl.ts` that this file has not
    // followed stops the tower here, rather than drawing 4096 parcels of token zero.
    for (const name of TOWER_INSTANCE_ATTRIBUTES) {
      if (geometry.getAttribute(name) === undefined) fail(`the shader reads an attribute ${name} nothing sets`);
    }

    const uniforms: Record<string, { value: unknown }> = {
      uCells: { value: textures.cells },
      uPalette: { value: textures.palette },
      uSched: { value: textures.sched },
      uBlocks: { value: textures.blocks },
      uGlyphs: { value: textures.glyphs },
      uFrame: { value: STILL_FRAME },
      uDetail: { value: 1 },
    };
    for (const name of [...TOWER_TEXTURE_UNIFORMS, ...TOWER_FRAME_UNIFORMS]) {
      if (uniforms[name] === undefined) fail(`the shader reads a uniform ${name} nothing sets`);
    }

    this.material = new ShaderMaterial({
      uniforms,
      vertexShader: parcelVertexGlsl(),
      fragmentShader: parcelFragmentGlsl(),
      side: DoubleSide,
    });

    this.mesh = new InstancedMesh(geometry, this.material, count);
    // The mesh spans the whole tower, so a frustum test on it can only ever cull the entire
    // collection at once — and it would do that against a bounding sphere that `place` has to
    // recompute anyway. Instances that should not draw are collapsed by `aShow` instead.
    this.mesh.frustumCulled = false;

    for (let f = 0; f < GLITCH_FRAMES; f++) this.bounds.push(frameStartMs(f));
  }

  /**
   * Rebuild every instance transform.
   *
   * `InstancedMesh.raycast` early-outs against a bounding sphere it computes once and caches, so
   * moving the parcels without recomputing it leaves picking testing yesterday's volume.
   */
  place(fn: (id: number, instance: number, m: Matrix4) => void): void {
    for (let i = 0; i < this.ids.length; i++) {
      this.matrix.identity();
      fn(this.ids[i] as number, i, this.matrix);
      this.mesh.setMatrixAt(i, this.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.computeBoundingSphere();
  }

  /** Push a new brightness and visibility for every parcel. Two 16 KB uploads, no draw calls. */
  present(fn: (id: number, instance: number) => Presentation): void {
    const dim = this.dimAttr.array as Float32Array;
    const show = this.showAttr.array as Float32Array;
    for (let i = 0; i < this.ids.length; i++) {
      const p = fn(this.ids[i] as number, i);
      dim[i] = p.dim;
      show[i] = p.show ? 1 : 0;
    }
    this.dimAttr.needsUpdate = true;
    this.showAttr.needsUpdate = true;
  }

  /**
   * Which frame of the loop a wall-clock reading lands on.
   *
   * The same walk over the same twelve boundaries the payload's player does, and for the same
   * reason: `frameStartMs` floors, so arithmetic on the durations would put the two renderers on
   * different frames for a millisecond at every boundary.
   */
  frameOf(ms: number): number {
    const t = ((ms % LOOP_MS) + LOOP_MS) % LOOP_MS;
    let frame = 0;
    for (let i = 1; i < this.bounds.length; i++) if (t >= (this.bounds[i] as number)) frame = i;
    return frame;
  }

  setFrame(frame: number): void {
    if (!Number.isInteger(frame) || frame < 0 || frame >= GLITCH_FRAMES) {
      fail(`frame ${frame} is not 0..${GLITCH_FRAMES - 1}`);
    }
    (this.material.uniforms.uFrame as { value: number }).value = frame;
  }

  /** The LOD slider: 1 is the honest crossover, higher holds the near path further away. */
  setDetail(detail: number): void {
    (this.material.uniforms.uDetail as { value: number }).value = detail;
  }

  /** The mesh's own geometry and material. The textures belong to `disposeTowerTextures`. */
  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
    this.mesh.dispose();
  }
}

/** The deepest block level the shader may ask for, re-exported so callers need not guess. */
export const PARCEL_BLOCK_TOP_LEVEL = BLOCK_TOP_LEVEL;
